import { collection, addDoc, updateDoc, doc, getDocs, query, where, orderBy, limit, onSnapshot, Timestamp } from 'firebase/firestore';
import { db } from './config';
import { logUserBan, logUserWarn, logAdminAction } from './activityLog';

export interface UserBan {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  bannedBy: string;
  bannedByName: string;
  reason: string;
  banType: 'temporary' | 'permanent';
  duration?: number; // in hours for temporary bans
  expiresAt?: Date;
  createdAt: Date;
  isActive: boolean;
}

export interface UserWarning {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  warnedBy: string;
  warnedByName: string;
  reason: string;
  severity: 'low' | 'medium' | 'high';
  createdAt: Date;
  isActive: boolean;
}

export interface FlaggedContent {
  id: string;
  contentId: string;
  contentType: 'post' | 'message' | 'profile' | 'event';
  contentAuthorId: string;
  contentAuthorName: string;
  flaggedBy: string;
  flaggedByName: string;
  reason: string;
  status: 'pending' | 'reviewed' | 'dismissed' | 'action_taken';
  reviewedBy?: string;
  reviewedByName?: string;
  reviewNotes?: string;
  createdAt: Date;
  reviewedAt?: Date;
}

export interface SiteAnnouncement {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'warning' | 'emergency';
  createdBy: string;
  createdByName: string;
  isActive: boolean;
  createdAt: Date | Timestamp;
  expiresAt?: Date | Timestamp | null;
}

// User Banning Functions
export const banUser = async (
  userId: string,
  userName: string,
  userEmail: string,
  bannedBy: string,
  bannedByName: string,
  reason: string,
  banType: 'temporary' | 'permanent' = 'permanent',
  duration?: number
): Promise<string> => {
  try {
    const expiresAt = banType === 'temporary' && duration 
      ? new Date(Date.now() + duration * 60 * 60 * 1000) 
      : undefined;

    const banData: Omit<UserBan, 'id'> = {
      userId,
      userName,
      userEmail,
      bannedBy,
      bannedByName,
      reason,
      banType,
      duration,
      expiresAt,
      createdAt: new Date(),
      isActive: true
    };

    const docRef = await addDoc(collection(db, 'userBans'), banData);
    
    // Update user profile to mark as banned
    await updateDoc(doc(db, 'profiles', userId), {
      isBanned: true,
      banReason: reason,
      bannedAt: new Date(),
      bannedBy: bannedByName
    });

    // Log the ban action
    await logUserBan(bannedBy, bannedByName, userId, userName, reason);

    return docRef.id;
  } catch (error) {
    console.error('Error banning user:', error);
    throw error;
  }
};

export const unbanUser = async (userId: string, unbannedBy: string, unbannedByName: string): Promise<void> => {
  try {
    // Update user profile to remove ban
    await updateDoc(doc(db, 'profiles', userId), {
      isBanned: false,
      banReason: null,
      bannedAt: null,
      bannedBy: null
    });

    // Deactivate all active bans for this user
    const bansQuery = query(
      collection(db, 'userBans'),
      where('userId', '==', userId),
      where('isActive', '==', true)
    );
    const bansSnapshot = await getDocs(bansQuery);
    
    const updatePromises = bansSnapshot.docs.map(doc => 
      updateDoc(doc.ref, { isActive: false })
    );
    await Promise.all(updatePromises);

    // Log the unban action
    await logAdminAction(unbannedBy, unbannedByName, 'User Unbanned', `Unbanned user ${userId}`);

  } catch (error) {
    console.error('Error unbanning user:', error);
    throw error;
  }
};

export const getUserBans = async (userId?: string): Promise<UserBan[]> => {
  try {
    let q = query(collection(db, 'userBans'), orderBy('createdAt', 'desc'));
    
    if (userId) {
      q = query(
        collection(db, 'userBans'),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      );
    }
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as UserBan));
  } catch (error) {
    console.error('Error fetching user bans:', error);
    return [];
  }
};

// User Warning Functions
export const warnUser = async (
  userId: string,
  userName: string,
  userEmail: string,
  warnedBy: string,
  warnedByName: string,
  reason: string,
  severity: 'low' | 'medium' | 'high' = 'medium'
): Promise<string> => {
  try {
    const warningData: Omit<UserWarning, 'id'> = {
      userId,
      userName,
      userEmail,
      warnedBy,
      warnedByName,
      reason,
      severity,
      createdAt: new Date(),
      isActive: true
    };

    const docRef = await addDoc(collection(db, 'userWarnings'), warningData);

    // Log the warning action
    await logUserWarn(warnedBy, warnedByName, userId, userName, reason);

    return docRef.id;
  } catch (error) {
    console.error('Error warning user:', error);
    throw error;
  }
};

export const getUserWarnings = async (userId?: string): Promise<UserWarning[]> => {
  try {
    let q = query(collection(db, 'userWarnings'), orderBy('createdAt', 'desc'));
    
    if (userId) {
      q = query(
        collection(db, 'userWarnings'),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      );
    }
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as UserWarning));
  } catch (error) {
    console.error('Error fetching user warnings:', error);
    return [];
  }
};

// Content Flagging Functions
export const flagContent = async (
  contentId: string,
  contentType: 'post' | 'message' | 'profile' | 'event',
  contentAuthorId: string,
  contentAuthorName: string,
  flaggedBy: string,
  flaggedByName: string,
  reason: string
): Promise<string> => {
  try {
    const flagData: Omit<FlaggedContent, 'id'> = {
      contentId,
      contentType,
      contentAuthorId,
      contentAuthorName,
      flaggedBy,
      flaggedByName,
      reason,
      status: 'pending',
      createdAt: new Date()
    };

    const docRef = await addDoc(collection(db, 'flaggedContent'), flagData);
    return docRef.id;
  } catch (error) {
    console.error('Error flagging content:', error);
    throw error;
  }
};

export const getFlaggedContent = async (status?: 'pending' | 'reviewed' | 'dismissed' | 'action_taken'): Promise<FlaggedContent[]> => {
  try {
    let q = query(collection(db, 'flaggedContent'), orderBy('createdAt', 'desc'));
    
    if (status) {
      q = query(
        collection(db, 'flaggedContent'),
        where('status', '==', status),
        orderBy('createdAt', 'desc')
      );
    }
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as FlaggedContent));
  } catch (error) {
    console.error('Error fetching flagged content:', error);
    return [];
  }
};

export const reviewFlaggedContent = async (
  flagId: string,
  status: 'dismissed' | 'action_taken',
  reviewedBy: string,
  reviewedByName: string,
  reviewNotes?: string
): Promise<void> => {
  try {
    await updateDoc(doc(db, 'flaggedContent', flagId), {
      status,
      reviewedBy,
      reviewedByName,
      reviewNotes,
      reviewedAt: new Date()
    });

    // Log the review action
    await logAdminAction(reviewedBy, reviewedByName, 'Content Review', `Reviewed flagged content: ${reviewNotes || status}`);
  } catch (error) {
    console.error('Error reviewing flagged content:', error);
    throw error;
  }
};

// Site Announcement Functions
export const createSiteAnnouncement = async (
  title: string,
  message: string,
  type: 'info' | 'warning' | 'emergency',
  createdBy: string,
  createdByName: string,
  expiresAt?: Date
): Promise<string> => {
  try {
    const announcementData: Omit<SiteAnnouncement, 'id'> = {
      title,
      message,
      type,
      createdBy,
      createdByName,
      isActive: true,
      createdAt: new Date(),
      expiresAt
    };

    const docRef = await addDoc(collection(db, 'siteAnnouncements'), announcementData);

    // Log the announcement creation
    await logAdminAction(createdBy, createdByName, 'Site Announcement', `Created announcement: ${title}`);

    return docRef.id;
  } catch (error) {
    console.error('Error creating site announcement:', error);
    throw error;
  }
};

export const getActiveAnnouncements = async (): Promise<SiteAnnouncement[]> => {
  try {
    // First get all active announcements without ordering to avoid index requirement
    const q = query(
      collection(db, 'siteAnnouncements'),
      where('isActive', '==', true)
    );
    
    const querySnapshot = await getDocs(q);
    const now = new Date();
    
    return querySnapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() } as SiteAnnouncement))
      .filter(announcement => 
        !announcement.expiresAt || announcement.expiresAt > now
      )
      .sort((a, b) => {
        const dateA = a.createdAt instanceof Date ? a.createdAt : a.createdAt.toDate();
        const dateB = b.createdAt instanceof Date ? b.createdAt : b.createdAt.toDate();
        return dateB.getTime() - dateA.getTime();
      });
  } catch (error) {
    console.error('Error fetching active announcements:', error);
    return [];
  }
};

// Emergency Controls
export const emergencyShutdown = async (adminId: string, adminName: string, reason: string): Promise<void> => {
  try {
    // Create emergency announcement
    await createSiteAnnouncement(
      '🚨 EMERGENCY SHUTDOWN',
      `The site has been temporarily shut down for maintenance. Reason: ${reason}`,
      'emergency',
      adminId,
      adminName
    );

    // Log the emergency action
    await logAdminAction(adminId, adminName, 'Emergency Shutdown', `Emergency shutdown initiated: ${reason}`, 'critical');

  } catch (error) {
    console.error('Error during emergency shutdown:', error);
    throw error;
  }
};

// Real-time listeners
export const subscribeToFlaggedContent = (callback: (content: FlaggedContent[]) => void) => {
  const q = query(collection(db, 'flaggedContent'), orderBy('createdAt', 'desc'));
  
  return onSnapshot(q, (snapshot) => {
    const content = snapshot.docs.map(doc => ({ 
      id: doc.id, 
      ...doc.data() 
    } as FlaggedContent));
    callback(content);
  });
};

export const subscribeToUserBans = (callback: (bans: UserBan[]) => void) => {
  const q = query(collection(db, 'userBans'), orderBy('createdAt', 'desc'));
  
  return onSnapshot(q, (snapshot) => {
    const bans = snapshot.docs.map(doc => ({ 
      id: doc.id, 
      ...doc.data() 
    } as UserBan));
    callback(bans);
  });
};

export const subscribeToUserWarnings = (callback: (warnings: UserWarning[]) => void) => {
  const q = query(collection(db, 'userWarnings'), orderBy('createdAt', 'desc'));
  
  return onSnapshot(q, (snapshot) => {
    const warnings = snapshot.docs.map(doc => ({ 
      id: doc.id, 
      ...doc.data() 
    } as UserWarning));
    callback(warnings);
  });
};
